import { ref, onMounted, onUnmounted, watch } from 'vue';
import type { SwiperContainer } from 'swiper/element';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useWindowSize } from '@vueuse/core';

export const usePackages = () => {
  gsap.registerPlugin(ScrollTrigger);

  const containerRef = ref<HTMLElement | undefined>();
  const swiperRef = ref<SwiperContainer | undefined>();
  const totalSlides = ref(0);
  const snap = ref<(valueToSnap: number) => number>();

  const scrollTrigger = ref<ReturnType<typeof ScrollTrigger.create>>();

  const { width: windowWidth } = useWindowSize();

  onMounted(() => {
    if (!containerRef.value || !swiperRef.value) return;

    swiperRef.value.initialize();
    swiperRef.value.classList.add('swiper-initialized');
    swiperRef.value.swiper.slideTo(0);

    totalSlides.value = swiperRef.value.swiper.slides.length;
    snap.value = gsap.utils.snap(1 / totalSlides.value);

    scrollTrigger.value = ScrollTrigger.create({
      trigger: containerRef.value,
      start: 'center center',
      end: '+=200%',
      pin: true,
      onEnter: (self) => {
        if (!swiperRef.value?.swiper || swiperRef.value?.swiper.destroyed) swiperRef.value?.initialize();

        const isBeginning = swiperRef.value?.swiper.isBeginning || false;
        const isEnd = swiperRef.value?.swiper.isEnd || false;
        if (isBeginning && isEnd) self.disable(true);
      },
      onEnterBack: (self) => {
        if (!swiperRef.value?.swiper || swiperRef.value?.swiper.destroyed) swiperRef.value?.initialize();

        const isBeginning = swiperRef.value?.swiper.isBeginning || false;
        const isEnd = swiperRef.value?.swiper.isEnd || false;
        if (isBeginning && isEnd) self.disable(true);
      },
      onUpdate: (self) => {
        const isBeginning = swiperRef.value?.swiper.isBeginning || false;
        const isEnd = swiperRef.value?.swiper.isEnd || false;
        if (isBeginning && isEnd) self.disable(true);

        let currentSlide = swiperRef.value?.swiper.activeIndex || 0;
        const updatedIndex = Math.round(snap.value!.call(this, self.progress) * totalSlides.value);

        if (updatedIndex !== currentSlide) {
          currentSlide = updatedIndex;
          swiperRef.value?.swiper.slideTo(currentSlide);
        }
      }
    });
  });

  onUnmounted(() => {
    scrollTrigger.value?.kill();
  });

  watch(
    windowWidth,
    () => {
      scrollTrigger.value?.enable(true, true);
      swiperRef.value?.swiper.slideTo(0);
    },
    { deep: true }
  );

  return {
    containerRef,
    swiperRef
  };
};
